import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import "../css/ContactTab.css";

const sanitize = (str) =>
  str?.replace(/[^\w\s\-@.]/gi, "").trim() || "User";

const ConversationsTab = ({ user, role }) => {
  const [conversations, setConversations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [filterUserType, setFilterUserType] = useState("all");
  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [replyMessages, setReplyMessages] = useState({});

  useEffect(() => {
    if (!user) return;

    console.log("📡 Subscribing to conversations for role:", role);

    const q =
      role === "superadmin"
        ? query(collection(db, "conversations"), orderBy("createdAt", "desc"))
        : query(
            collection(db, "conversations"),
            where("email", "==", user.email),
            orderBy("createdAt", "desc")
          );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("✅ Conversations loaded:", data);
        setConversations(data);
      },
      (err) => {
        console.error("❌ Firestore error in ConversationsTab:", err);
      }
    );

    return () => unsub();
  }, [user, role]);

  useEffect(() => {
    let data = conversations;

    if (filterUserType !== "all") {
      data = data.filter((c) => c.userType === filterUserType);
    }

    if (filterUnreadOnly) {
      data = data.filter((c) => {
        const latest = c.messages?.[c.messages.length - 1];
        return latest?.from === "user";
      });
    }

    if (search.trim()) {
      const lower = search.toLowerCase();
      data = data.filter(
        (c) =>
          c.name?.toLowerCase().includes(lower) ||
          c.email?.toLowerCase().includes(lower)
      );
    }

    setFiltered(data);
  }, [conversations, search, filterUserType, filterUnreadOnly]);

  return (
    <div>
      <h2>📬 Conversations</h2>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <input
          type="text"
          placeholder="Search by name/email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />

        <select
          value={filterUserType}
          onChange={(e) => setFilterUserType(e.target.value)}
        >
          <option value="all">All</option>
          <option value="registered">Registered</option>
          <option value="guest">Guest</option>
        </select>

        <label>
          <input
            type="checkbox"
            checked={filterUnreadOnly}
            onChange={(e) => setFilterUnreadOnly(e.target.checked)}
          />{" "}
          Unread only
        </label>
      </div>

      {filtered.length === 0 ? (
        <p>No conversations found.</p>
      ) : (
        filtered.map((msg) => {
          const isExpanded = expandedId === msg.id;
          const latest = msg.messages?.[msg.messages.length - 1];
          const safeName = sanitize(msg.name);
          const safeEmail = sanitize(msg.email);
          const safePreview = latest?.message
            ? sanitize(latest.message).slice(0, 100)
            : "";

          return (
            <div
              key={msg.id}
              className="contact-item"
              onClick={() =>
                setExpandedId(isExpanded ? null : msg.id)
              }
            >
              <div className="contact-header">
                <div>
                  <p>
                    <strong>{safeName}</strong> &lt;{safeEmail}&gt; &lt;
                    {msg.userType}&gt;
                  </p>
                  <p className="contact-message-preview">
                    {safePreview || "(No message)"}
                  </p>
                </div>
                <p style={{ color: "#aaa", fontSize: "0.85rem" }}>
                  {latest?.timestamp?.toDate?.().toLocaleString?.() || ""}
                </p>
              </div>

              {isExpanded && (
                <div className="contact-details">
                  {msg.messages?.map((m, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor:
                          m.from === "admin" ? "#1f1f1f" : "#2c3e50",
                        padding: "1rem",
                        borderRadius: "8px",
                        color: "#f1f1f1",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <strong>
                          {m.from === "admin" ? "Admin" : "User"}
                        </strong>
                        <span style={{ fontSize: "0.85rem", color: "#ccc" }}>
                          {m.timestamp?.toDate?.().toLocaleString?.() || ""}
                        </span>
                      </div>
                      <p style={{ marginTop: "0.5rem" }}>
                        {sanitize(m.message)}
                      </p>
                    </div>
                  ))}

                  <textarea
                    rows="2"
                    placeholder={`Reply to ${safeName}`}
                    value={replyMessages[msg.id] || ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setReplyMessages((prev) => ({
                        ...prev,
                        [msg.id]: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      marginBottom: "0.5rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  />
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const reply = replyMessages[msg.id]?.trim();
                      if (!reply) return toast.info("Reply cannot be empty.");

                      try {
                        await updateDoc(doc(db, "conversations", msg.id), {
                          messages: arrayUnion({
                            from: "admin",
                            message: reply,
                            timestamp: new Date(),
                          }),
                        });
                        toast.success("✅ Reply sent.");
                        setReplyMessages((prev) => ({
                          ...prev,
                          [msg.id]: "",
                        }));
                      } catch (err) {
                        console.error("❌ Error sending reply:", err);
                        toast.error("❌ Failed to send reply.");
                      }
                    }}
                    className="btn"
                  >
                    📤 Send Reply
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default ConversationsTab;
